import { create } from 'zustand';
import type { User, ChatSession, Document, GeneratedDocument } from '@/types';

interface AppState {
  // User state
  user: User | null;
  setUser: (user: User | null) => void;

  // Chat state
  currentChatId: string | null;
  setCurrentChatId: (id: string | null) => void;
  chatSessions: ChatSession[];
  setChatSessions: (sessions: ChatSession[]) => void;

  // Document state
  documents: Document[];
  setDocuments: (docs: Document[]) => void;
  addDocument: (doc: Document) => void;
  removeDocument: (id: string) => void;

  // UI state
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Token state
  tokenBalance: number;
  setTokenBalance: (balance: number) => void;

  // Generated documents state
  generatedDocuments: GeneratedDocument[];
  setGeneratedDocuments: (docs: GeneratedDocument[]) => void;
  addGeneratedDocument: (doc: GeneratedDocument) => void;
  removeGeneratedDocument: (id: string) => void;
  updateGeneratedDocumentInStore: (doc: GeneratedDocument) => void;
}

export const useAppStore = create<AppState>((set) => ({
  // User
  user: null,
  setUser: (user) => set({ user }),

  // Chat
  currentChatId: null,
  setCurrentChatId: (id) => set({ currentChatId: id }),
  chatSessions: [],
  setChatSessions: (sessions) => set({ chatSessions: sessions }),

  // Documents
  documents: [],
  setDocuments: (docs) => set({ documents: docs }),
  addDocument: (doc) => set((state) => ({ documents: [...state.documents, doc] })),
  removeDocument: (id) => set((state) => ({
    documents: state.documents.filter((d) => d.id !== id)
  })),

  // UI
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  // Tokens
  tokenBalance: 0,
  setTokenBalance: (balance) => set({ tokenBalance: balance }),

  // Generated documents
  generatedDocuments: [],
  setGeneratedDocuments: (docs) => set({ generatedDocuments: docs }),
  addGeneratedDocument: (doc) => set((state) => ({ generatedDocuments: [doc, ...state.generatedDocuments] })),
  removeGeneratedDocument: (id) => set((state) => ({ generatedDocuments: state.generatedDocuments.filter(d => d.id !== id) })),
  updateGeneratedDocumentInStore: (doc) => set((state) => ({ generatedDocuments: state.generatedDocuments.map(d => d.id === doc.id ? doc : d) })),
}));
